import React, { useState, useEffect } from "react";
import { Button } from "reactstrap";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import PropTypes from "prop-types";

import { getAccountType } from "../types";
import ModalHolder from "../../Modal/ModalHolder";
import HoldersForm from "../HoldersForm";
import ModalConfirm from "../../Modal/ModalConfirm";
import { CircularProgress } from "../../../..";
import BeneficiariesForm from "../BeneficiariesForm";
import { AccountMode } from "../../../../utils/Helper";

const BeneficiariesTab = ({
  holders,
  account,
  accountId,
  setAccountData,
  services,
  userType,
  isReadOnly,
  mode,
  setRedirect,
  Link,
  newOnboardingVersion = false,
}) => {
  const [loadingSave, setLoadingSave] = useState(false);
  const [collapsedHolder, setCollapsedHolder] = useState([]);
  const [changedHolder, setChangedHolder] = useState({});
  const [modalHolder, setModalHolder] = useState({
    open: false,
    saving: false,
  });
  const [modalConfirm, setModalConfirm] = useState({
    open: false,
    title: "",
    callback: undefined,
    loading: false,
    holderId: undefined,
  });

  useEffect(() => {
    if (holders) {
      const newCollapsedHolder = holders.map(() => ({
        open: false,
        canEdit: false,
      }));
      setCollapsedHolder(newCollapsedHolder);
    }
  }, [holders]);

  const toggleModalHolder = () =>
    setModalHolder((m) => ({
      open: !m.open,
      saving: false,
    }));

  const handleDeleteHolder = (holderId) => {
    const allHolders = [...holders];
    const postAccount = {
      AccountId: accountId,
      AccountOnboardingId: accountId,
      Holder: [
        {
          PersonId: holderId,
          deleteHolder: true,
        },
      ],
    };
    const body = {
      account: postAccount,
      accountChangeType: getAccountType.Beneficiaries,
    };
    setModalConfirm((m) => ({
      ...m,
      loading: true,
    }));
    if (mode !== AccountMode.New) {
      const updatedBody = {
        ...body, 
        account: {
          ...body.account, 
          isOnboardingAccount: newOnboardingVersion ? true : false,
        },
      };
      services.fetchAccountUpdate(updatedBody, (response) => {
        if (response && response.result && response.result.Code === 200) {
          let index = -1;
          allHolders.forEach((holder, i) => {
            if (parseInt(holderId, 10) === parseInt(holder.AccountHolderId, 10)) {
              index = i;
            }
          });
          if (index > -1) {
            allHolders.splice(index, 1);
            collapsedHolder.splice(index, 1);
          }
          setModalConfirm({
            open: false,
            title: "",
            holderId: undefined,
            callback: undefined,
            loading: false,
          });
          setAccountData(
            {
              AccountId: accountId,
              AccountInfoHolder: allHolders,
            },
            true
          );
        } else {
          setModalConfirm((m) => ({
            ...m,
            loading: false,
          }));
        }
      });
    } else {
      services.fetchNewAccount(body, (response) => {
        if (response && response.result && response.result.Code === 200) {
          let index = -1;
          allHolders.forEach((holder, i) => {
            if (parseInt(holderId, 10) === parseInt(holder.AccountHolderId, 10)) {
              index = i;
            }
          });
          if (index > -1) {
            allHolders.splice(index, 1);
            collapsedHolder.splice(index, 1);
          }
          setModalConfirm({
            open: false,
            title: "",
            holderId: undefined,
            callback: undefined,
            loading: false,
          });
          setAccountData(
            {
              AccountId: accountId,
              AccountInfoHolder: allHolders,
            },
            true
          );
        } else {
          setModalConfirm((m) => ({
            ...m,
            loading: false,
          }));
        }
      });
    }
  };

  function preCheckObjectEmpty(object) {
    if (!object || Object.keys(object).length === 0) return false;
  
    return Object.values(object).some((value) => {
      return value && typeof value === 'object' && Object.keys(value).length > 0;
    });
  }
  const handleRemoveHolder = (holderId) =>
    setModalConfirm((m) => ({
      ...m,
      open: true,
      title: "Are you sure you want to remove the selected Beneficiary?",
      holderId,
      callback: () => handleDeleteHolder(holderId),
    }));

  const toggleModalConfirm = () =>
    setModalConfirm({
      open: false,
      title: "",
      holderId: undefined,
      callback: undefined,
      loading: false,
    });

  const revert = () => setChangedHolder({});

  const handleSave = (newHolders) => {
    const allHolders = [...holders];
    const newAccountHolders = Object.keys(newHolders).map((key) => newHolders[key]);
    const postAccount = {
      AccountId: accountId,
      AccountOnboardingId: accountId,
      Holder: newAccountHolders,
    };
    const body = {
      account: postAccount,
      accountChangeType: getAccountType.Beneficiaries,
    };
    setLoadingSave(true);
    if (mode !== AccountMode.New) {
      const updatedBody = {
        ...body, 
        account: {
          ...body.account, 
          isOnboardingAccount: newOnboardingVersion ? true : false,
        },
      };
      services.fetchAccountUpdate(updatedBody, (response) => {
        if (response && response.result && response.result.Code === 200) {
          Object.keys(newHolders).forEach((key) => {
            let index = -1;
            const newHolder = {
              AccountHolderId: newHolders[key].PersonId,
              AccountHolderName: newHolders[key].PersonName,
              AccountHolderPersonType: newHolders[key].PersonType,
              AccountHolderRelationship: newHolders[key].HoldersRelationship,
              AccountHolderRelationshipJuridical: newHolders[key].HoldersRelationshipJuridical,
              AccountHolderIsController: newHolders[key].HoldersIsController,
              AccountHolderIsManager: newHolders[key].HoldersIsManager,
              IsMainHolder: newHolders[key].isMainHolder,
              HoldersOrderWeb: newHolders[key].HoldersOrderWeb,
              HoldersOrderManaged: newHolders[key].HoldersOrderManaged,
              HoldersOrderWritten: newHolders[key].HoldersOrderWritten,
              HoldersOrderViaLink: newHolders[key].HoldersOrderViaLink,
              AccountHolderIsBeneficiary: newHolders[key].AccountHolderIsBeneficiary,
              AccountHolderEmail: response.newHolderInfo.AccountHolderEmail,
              AccountHolderAddress: response.newHolderInfo.AccountHolderAddress,
              AccountHolderCountryId: response.newHolderInfo.AccountHolderCountryId,
              AccountHolderCountryName: response.newHolderInfo.AccountHolderCountryName,
              AccountHolderByAdvisor: userType == "Advisor",
            };
            allHolders.forEach((holder, i) => {
              if (parseInt(newHolder.AccountHolderId, 10) === parseInt(holder.AccountHolderId, 10)) {
                index = i;
              }
            });
            if (index > -1) {
              allHolders[index] = {
                ...allHolders[index],
                ...newHolder,
              };
              collapsedHolder[index].canEdit = false;
            } else {
              holders.push(newHolder);
              collapsedHolder.push({ open: false, canEdit: false });
            }
          });
          setLoadingSave(false);
          setChangedHolder({});
          setAccountData(
            {
              AccountId: accountId,
              AccountInfoHolder: allHolders,
            },
            true
          );
        } else {
          setLoadingSave(false);
        }
      });
    } else {
      services.fetchNewAccount(body, (response) => {
        if (response && response.result && response.result.Code === 200) {
          Object.keys(newHolders).forEach((key) => {
            let index = -1;
            const newHolder = {
              AccountHolderId: newHolders[key].PersonId,
              AccountHolderName: newHolders[key].PersonName,
              AccountHolderPersonType: newHolders[key].PersonType,
              AccountHolderStatus: newHolders[key].PersonStatus,
              AccountHolderRelationship: newHolders[key].HoldersRelationship,
              AccountHolderRelationshipJuridical: newHolders[key].HoldersRelationshipJuridical,
              AccountHolderIsController: newHolders[key].HoldersIsController,
              AccountHolderIsManager: newHolders[key].HoldersIsManager,
              IsMainHolder: newHolders[key].isMainHolder,
              HoldersOrderWeb: newHolders[key].HoldersOrderWeb,
              HoldersOrderManaged: newHolders[key].HoldersOrderManaged,
              HoldersOrderWritten: newHolders[key].HoldersOrderWritten,
              HoldersOrderViaLink: newHolders[key].HoldersOrderViaLink,
              AccountHolderIsBeneficiary: newHolders[key].AccountHolderIsBeneficiary,
              AccountHolderEmail: response.newHolderInfo.AccountHolderEmail,
              AccountHolderAddress: response.newHolderInfo.AccountHolderAddress,
              AccountHolderCountryId: response.newHolderInfo.AccountHolderCountryId,
              AccountHolderCountryName: response.newHolderInfo.AccountHolderCountryName,
              AccountHolderByAdvisor: userType == "Advisor",
            };
            allHolders.forEach((holder, i) => {
              if (parseInt(newHolder.AccountHolderId, 10) === parseInt(holder.AccountHolderId, 10)) {
                index = i;
              }
            });
            if (index > -1) {
              allHolders[index] = {
                ...allHolders[index],
                ...newHolder,
              };
              collapsedHolder[index].canEdit = false;
            } else {
              holders.push(newHolder);
              allHolders.push(newHolder);
              collapsedHolder.push({ open: false, canEdit: false });
            }
          });
          setLoadingSave(false);
          setChangedHolder({});
          setAccountData(
            {
              AccountId: accountId,
              AccountInfoHolder: allHolders,
            },
            true
          );
        } else {
          setLoadingSave(false);
        }
      });
    }
  };

  const toggleHolder = (index, toggle) => {
    const newCollapsedHolder = [...collapsedHolder];
    if (toggle) {
      for (let i = 0; i < newCollapsedHolder.length; i += 1) {
        if (i === index) {
          newCollapsedHolder[i] = {
            open: !newCollapsedHolder[i].open,
            canEdit: false,
          };
        } else {
          newCollapsedHolder[i] = {
            open: false,
            canEdit: false,
          };
        }
      }
    } else {
      newCollapsedHolder[index].canEdit = !newCollapsedHolder[index].canEdit;
    }
    setCollapsedHolder(newCollapsedHolder);
  };

  const onChange = (index, name, value) => {

    const newChangedHolder = { ...changedHolder };

    if (!newChangedHolder[holders[index].AccountHolderId]) {
      newChangedHolder[holders[index].AccountHolderId] = {
        PersonId: holders[index].AccountHolderId,
        PersonName: holders[index].AccountHolderName,
        PersonType: holders[index].AccountHolderPersonType,
        PersonStatus: holders[index].AccountHolderStatus,
        HoldersRelationship: holders[index].AccountHolderRelationship,
        HoldersRelationshipJuridical: holders[index].AccountHolderRelationshipJuridical,
        HoldersIsController: holders[index].AccountHolderIsController,
        HoldersIsManager: holders[index].AccountHolderIsManager,
        isMainHolder: holders[index].IsMainHolder,
        deleteHolder: false,
        HoldersOrderWeb: holders[index].HoldersOrderWeb,
        HoldersOrderWritten: holders[index].HoldersOrderWritten,
        HoldersOrderManaged: holders[index].HoldersOrderManaged,
        HoldersOrderViaLink: holders[index].HoldersOrderViaLink,
        [name]: value,
      };
    } else {
      newChangedHolder[holders[index].AccountHolderId][name] = value;
    }
    if (name === "HoldersOrderManaged") {
      newChangedHolder[holders[index].AccountHolderId].HoldersOrderViaLink = false;
      newChangedHolder[holders[index].AccountHolderId].HoldersOrderWeb = false;
      newChangedHolder[holders[index].AccountHolderId].HoldersOrderWritten = false;
    }
    setChangedHolder(newChangedHolder);
  };

  if (!holders || collapsedHolder.length !== holders.length) {
    return <CircularProgress sm />;
  }

  let existsMainHolder = false;
  account.AccountInfoHolder.forEach((h) => {
    const changed = changedHolder[h.AccountHolderId];
    const isMainHolder = changed && changed.hasOwnProperty("isMainHolder")
      ? changed.isMainHolder
      : h.IsMainHolder;
    existsMainHolder = existsMainHolder || isMainHolder;
  });



  return (
    <div>
      <BeneficiariesForm
        holders={holders.filter((holder) => {
          if (mode === AccountMode.New) {
            return holder.AccountHolderIsBeneficiary;
          }
          return holder.AccountHolderIsBeneficiary && !holder.AccountHolderByAdvisor;
        })}
        changedHolder={changedHolder}
        collapsedHolder={collapsedHolder}
        onChange={onChange}
        toggleHolder={toggleHolder}
        account={account}
        existsMainHolder={existsMainHolder}
        handleDeleteHolder={handleRemoveHolder}
        role={"Beneficiary"}
        isReadOnly={isReadOnly}
      />
      {!isReadOnly && (
        <Button
          disabled={loadingSave}
          color="primary"
          style={{ marginRight: 10 }}
          onClick={toggleModalHolder}
        >
          Add New Beneficiary
        </Button>
      )}
      {!isReadOnly && (
        <Button style={{ marginRight: 10 }} disabled={loadingSave} onClick={() => revert()}>
          <i className="fa fa-undo" /> Revert
        </Button>
      )}
      {!isReadOnly && (
        <LaddaButton
          className="btn btn-ladda btn-primary"
          loading={loadingSave}
          disabled={!preCheckObjectEmpty(changedHolder) || !existsMainHolder}
          onClick={() => handleSave(changedHolder)}
          data-style={EXPAND_RIGHT}
        >
          Save
        </LaddaButton>
      )}
      <ModalHolder
        {...modalHolder}
        toggle={toggleModalHolder}
        onSave={handleSave}
        account={account}
        saving={loadingSave}
        holders={holders}
        services={services}
        role="Beneficiary"
        Link={Link}
      />
      <ModalConfirm
        open={modalConfirm.open}
        toggle={toggleModalConfirm}
        title={modalConfirm.title}
        callback={modalConfirm.callback}
        saving={modalConfirm.loading}
      />
    </div>
  );
};

BeneficiariesTab.propTypes = {
  holders: PropTypes.array.isRequired,
  accountId: PropTypes.any.isRequired,
  account: PropTypes.object.isRequired,
  setAccountData: PropTypes.func.isRequired,
  services: PropTypes.object.isRequired,
  userType: PropTypes.string.isRequired,
};

export default BeneficiariesTab;
